import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cx } from '@/lib/cx';

import { Spinner } from './Spinner';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'ghost' | 'danger' | 'subtle';
export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Full width. Used for the primary action in a form or a drawer footer. */
  block?: boolean;
  /**
   * Shows a spinner and disables the button.
   *
   * The label stays visible rather than being replaced, so the button does not
   * change width mid-click and move whatever is next to it.
   */
  loading?: boolean;
  /** Replaced by the spinner while loading. */
  icon?: ReactNode;
  iconAfter?: ReactNode;
  type?: 'button' | 'submit' | 'reset';
  children?: ReactNode;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    block = false,
    loading = false,
    icon,
    iconAfter,
    type = 'button',
    disabled,
    className,
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      // A loading button that can still be clicked submits twice.
      disabled={disabled ?? loading}
      aria-busy={loading ? true : undefined}
      className={cx(
        styles.btn,
        styles[variant],
        size !== 'md' && styles[size],
        block && styles.block,
        className,
      )}
    >
      <span className={styles.label}>
        {loading ? <Spinner size={16} label={null} /> : icon}
        {children}
        {!loading && iconAfter}
      </span>
    </button>
  );
});
